import { type SafeEditorErrorSummary } from './safe-editor-error';

/**
 * The host-supplied logger adapter for `@heidi/editor` (OpenSpec
 * `monorepo-kit-editor` task 4.10; design D8).
 *
 * The package owns no transport. It never calls `console`, never imports an
 * application logger, and never hands a raw error or any document-derived
 * string to one — a TipTap/ProseMirror failure can echo note content in its
 * message, so a raw error in a log payload ships PHI to the log sink. What
 * crosses instead is `EditorLogRecord`: a closed shape whose every field is
 * content-free by construction.
 *
 * The two landed 4.10 behavior-policy slices
 * (`4.10-convert-schema-guard-log-hoist`,
 * `4.10-convert-section-safe-error-envelope`) each replaced one raw-error log
 * payload with `SafeEditorErrorSummary` and left the transport app-side. This
 * module is the package-side half those slices presuppose: the typed sink the
 * host supplies, plus the adapter that narrows every record before it crosses.
 *
 * The adapter is deliberately DEFENSIVE rather than trusting. A type is a
 * compile-time promise, and PHI safety here is a runtime property — so
 * `createSafeEditorLogger` rebuilds each record field by field from an
 * allowlist instead of forwarding the object it was handed. A poisoned error
 * name, an extra property, a string measure: none of them reach the host sink.
 */

/** Severity the package requests; the host maps it onto its own transport. */
export type EditorLogLevel = 'warn' | 'error';

/**
 * The closed set of events the package may log. Codes are stable, content-free
 * and query-shaped; adding one is a deliberate edit here, which is what keeps
 * the set auditable. A record carrying an unlisted code is DROPPED, not
 * forwarded with the code stripped — fail-closed, because an unrecognised code
 * means the record was not built by this package.
 */
export type EditorLogCode =
  | 'content_apply_failed'
  | 'selection_restore_failed'
  | 'paste_parse_failed'
  | 'clipboard_payload_failed'
  | 'canonical_render_failed';

const LOG_CODES: ReadonlySet<string> = new Set<EditorLogCode>([
  'content_apply_failed',
  'selection_restore_failed',
  'paste_parse_failed',
  'clipboard_payload_failed',
  'canonical_render_failed',
]);

const LOG_LEVELS: ReadonlySet<string> = new Set<EditorLogLevel>(['warn', 'error']);

const CLASSIFICATIONS: ReadonlySet<string> = new Set<
  SafeEditorErrorSummary['classification']
>(['invalid-content', 'selection-range', 'unknown']);

/**
 * A content-free log record. Every field is either a closed union or a number:
 * there is no free-text field on this type, and the adapter enforces that at
 * runtime as well.
 */
export interface EditorLogRecord {
  level: EditorLogLevel;
  code: EditorLogCode;
  /** The typed safe envelope, when the record reports a failure. */
  error?: SafeEditorErrorSummary;
  /**
   * Content-free numeric measures — lengths, counts, positions. Numbers only:
   * a length is safe, the string it measured is not.
   */
  measures?: Readonly<Record<string, number>>;
}

/** The host-supplied sink. Supplying none is supported and means "no logging". */
export type EditorLogger = (record: EditorLogRecord) => void;

/**
 * A JavaScript identifier, capped. Error names and measure keys are identifiers
 * by construction (`Error`, `RangeError`, the `typeof` of a non-Error throw,
 * `contentLength`), so anything else was not produced by a constructor or a
 * literal key in this package and is refused. This is what stops
 * `error.name = <note text>` — a writable property on every Error — from
 * riding the envelope out.
 */
const IDENTIFIER = /^[A-Za-z_$][A-Za-z0-9_$]{0,63}$/;

/** Replaces a name that failed the identifier test; never the rejected value. */
const REDACTED_NAME = 'NonIdentifierErrorName';

const toSafeMeasures = (
  measures: EditorLogRecord['measures']
): Readonly<Record<string, number>> | undefined => {
  if (!measures || typeof measures !== 'object') return undefined;
  const safe: Record<string, number> = {};
  for (const [key, value] of Object.entries(measures)) {
    if (!IDENTIFIER.test(key)) continue;
    if (typeof value !== 'number' || !Number.isFinite(value)) continue;
    safe[key] = value;
  }
  return Object.keys(safe).length > 0 ? safe : undefined;
};

const toSafeError = (
  error: EditorLogRecord['error']
): SafeEditorErrorSummary | undefined => {
  if (!error || typeof error !== 'object') return undefined;
  const name =
    typeof error.name === 'string' && IDENTIFIER.test(error.name)
      ? error.name
      : REDACTED_NAME;
  const classification = CLASSIFICATIONS.has(error.classification)
    ? error.classification
    : 'unknown';
  return { name, classification };
};

/**
 * Narrow a record to the fields that may cross the logging seam. Returns
 * `null` when the record cannot be made safe (unknown code), which the adapter
 * treats as "drop it".
 *
 * Exported for the adversarial PHI-sentinel suite, which drives it directly
 * with poisoned records; production code goes through `createSafeEditorLogger`.
 */
export const toSafeEditorLogRecord = (
  record: EditorLogRecord
): EditorLogRecord | null => {
  if (!record || typeof record !== 'object') return null;
  if (!LOG_CODES.has(record.code)) return null;
  const safe: EditorLogRecord = {
    level: LOG_LEVELS.has(record.level) ? record.level : 'error',
    code: record.code,
  };
  const error = toSafeError(record.error);
  if (error) safe.error = error;
  const measures = toSafeMeasures(record.measures);
  if (measures) safe.measures = measures;
  return safe;
};

/**
 * Wrap a host sink so only narrowed records reach it, and so a throwing or
 * absent host logger can never break an editor operation — every call site is
 * inside a failure path already.
 *
 * The narrowing runs inside the guard too, not just the sink call: reading a
 * field off a record is enough to run a getter, so a record with a throwing
 * accessor would otherwise escape `log()` and surface inside the catch handler
 * that called it. A record that throws while being narrowed is dropped.
 *
 * With no sink this returns a no-op: the package logs NOTHING by default, which
 * is what makes adding the seam a zero-delta change for every current consumer.
 */
export const createSafeEditorLogger = (
  sink: EditorLogger | undefined | null
): EditorLogger => {
  if (typeof sink !== 'function') return () => undefined;
  return (record) => {
    try {
      const safe = toSafeEditorLogRecord(record);
      if (!safe) return;
      sink(safe);
    } catch {
      // A host transport failure — or a hostile record that throws while being
      // narrowed — is not an editor failure. Swallowed without logging: there
      // is no second sink to log it to, by design.
    }
  };
};
